import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import accountService from '../services/accountService';
import transactionService from '../services/transactionService';
import Card from '../components/common/Card';

const Transfer = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [formData, setFormData] = useState({
    from_account_id: '',
    to_account_number: '',
    amount: '',
    description: '',
  });
  const [loading, setLoading] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [verificationCheck, setVerificationCheck] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [accountsData, beneficiariesData] = await Promise.all([
        accountService.getAccounts(),
        transactionService.getBeneficiaries(),
      ]);
      setAccounts(accountsData);
      setBeneficiaries(beneficiariesData);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    setSuccess('');
    setVerificationCheck(null);
  };

  const selectBeneficiary = (accountNumber) => {
    setFormData({ ...formData, to_account_number: accountNumber });
    setError('');
    setSuccess('');
    setVerificationCheck(null);
  };

  const checkVerificationRequired = async () => {
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError('Please enter a valid amount');
      return false;
    }

    try {
      setVerificationLoading(true);
      const check = await transactionService.checkVerificationRequired(
        'domestic',
        parseFloat(formData.amount)
      );
      setVerificationCheck(check);
      
      if (!check.can_proceed) {
        setShowVerificationModal(true);
        setVerificationLoading(false);
        return false;
      }
      
      setVerificationLoading(false);
      return true;
    } catch (error) {
      console.error('Error checking verification:', error);
      setVerificationLoading(false);
      // If verification check fails, proceed anyway
      return true;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setVerificationCheck(null);

    // Validate form
    if (!formData.from_account_id) {
      setError('Please select a source account');
      return;
    }
    if (!formData.to_account_number || formData.to_account_number.length < 12) {
      setError('Please enter a valid 12-digit account number');
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    // Check verification
    const isVerified = await checkVerificationRequired();
    if (!isVerified) {
      return;
    }

    setLoading(true);

    try {
      const response = await transactionService.createTransfer({
        from_account_id: parseInt(formData.from_account_id),
        to_account_number: formData.to_account_number,
        amount: parseFloat(formData.amount),
        description: formData.description,
      });

      setSuccess(`✅ Transfer successful! Reference: ${response.reference_number}`);
      setFormData({
        from_account_id: '',
        to_account_number: '',
        amount: '',
        description: '',
      });
      setVerificationCheck(null);
    } catch (err) {
      // Check if it's a verification error
      if (err.response?.data?.needs_verification) {
        setError(`🔒 Verification Required: ${err.response.data.message}`);
        setVerificationCheck({
          can_proceed: false,
          message: err.response.data.message,
          needs_verification: true,
          current_step: err.response.data.current_step
        });
        setShowVerificationModal(true);
      } else {
        setError(err.response?.data?.error || 'Transfer failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToVerification = () => {
    navigate('/verification-status', { 
      state: { 
        step: verificationCheck?.current_step || 1,
        message: verificationCheck?.message 
      }
    });
  };

  const handleSkipVerification = async () => {
    try {
      // Check current verification status
      const status = await transactionService.getVerificationStatus();
      if (status.is_verification_completed) {
        setSuccess('✅ Your account is already fully verified! You can proceed with the transfer.');
        setShowVerificationModal(false);
        // Retry the transfer
        setTimeout(() => {
          handleSubmit(new Event('submit'));
        }, 1000);
      } else {
        setError(`You need to complete step ${status.next_required_step}/5 verification first.`);
      }
    } catch (error) {
      console.error('Error checking verification status:', error);
    }
  };

  const VerificationModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center mb-6">
          <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mr-4">
            <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <h3 className="text-xl font-bold text-neutral-900">Verification Required</h3>
            <p className="text-sm text-neutral-600">Account verification needed for transfers</p>
          </div>
        </div>

        <div className="mb-6">
          <div className="bg-primary-50 border border-primary-200 rounded-xl p-4 mb-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-primary-500 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-primary-800 font-medium">Verification</p>
                <p className="text-primary-600 text-sm">Required_</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex space-x-3">
          <button
            onClick={handleProceedToVerification}
            className="flex-1 bg-gradient-to-r from-primary-600 to-accent-600 text-white py-3 rounded-xl font-semibold hover:from-primary-700 hover:to-accent-700 transition"
          >
            Complete Verification
          </button>
          <button
            onClick={() => setShowVerificationModal(false)}
            className="flex-1 bg-white text-neutral-700 py-3 rounded-xl border-2 border-neutral-200 font-semibold hover:border-neutral-300 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-neutral-900 mb-2">Transfer Money</h1>
          <p className="text-neutral-600">Send money securely to any account</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Transfer Form */}
          <div className="lg:col-span-2">
            <Card className="bg-white rounded-2xl shadow-lg border border-neutral-100 p-8">
              {error && !verificationCheck?.needs_verification && (
                <div className="bg-accent-50 border border-accent-200 text-accent-700 px-6 py-4 rounded-xl mb-6 flex items-center">
                  <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  {error}
                </div>
              )}

              {verificationCheck?.needs_verification && (
                <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-6 py-4 rounded-xl mb-6">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="font-semibold">Verification Required</p>
                      <button
                        onClick={handleProceedToVerification}
                        className="mt-2 text-primary-600 hover:text-primary-700 font-medium text-sm"
                      >
                        Click here to complete verification →
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {success && (
                <div className="bg-success-50 border border-success-200 text-success-700 px-6 py-4 rounded-xl mb-6 flex items-center">
                  <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  {success}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-3">
                    From Account *
                  </label>
                  <select
                    name="from_account_id"
                    value={formData.from_account_id}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition bg-white text-neutral-700"
                  >
                    <option value="">Select an account</option>
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.account_type} - {account.account_number} (${account.balance})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-3">
                    To Account Number *
                  </label>
                  <input
                    type="text"
                    name="to_account_number"
                    value={formData.to_account_number}
                    onChange={handleChange}
                    required
                    placeholder="Enter 12-digit account number"
                    maxLength="12"
                    minLength="12"
                    className="w-full px-4 py-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition bg-white text-neutral-700 placeholder-neutral-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-3">
                    Amount ($) *
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-neutral-500 font-semibold">
                      $
                    </span>
                    <input
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={handleChange}
                      required
                      min="0.01"
                      step="0.01"
                      placeholder="0.00"
                      className="w-full pl-10 pr-4 py-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition bg-white text-neutral-700"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-neutral-700 mb-3">
                    Description (Optional)
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="3"
                    placeholder="What's this transfer for?"
                    className="w-full px-4 py-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition bg-white text-neutral-700 placeholder-neutral-400 resize-none"
                  />
                </div>

                <div className="flex space-x-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-gradient-to-r from-primary-600 to-accent-600 text-white py-3 rounded-xl hover:from-primary-700 hover:to-accent-700 transition transform hover:scale-105 disabled:from-neutral-300 disabled:to-neutral-400 disabled:cursor-not-allowed disabled:transform-none font-semibold"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </div>
                    ) : (
                      'Transfer Money'
                    )}
                  </button>
                </div>
              </form>             
            </Card>
          </div>

          {/* Beneficiaries Sidebar */}
          <div>
            <Card className="bg-white rounded-2xl shadow-lg border border-neutral-100 p-6">
              <div className="mb-6">
                <h3 className="text-xl font-bold text-neutral-900 mb-2">Saved Beneficiaries</h3>
                <p className="text-sm text-neutral-600">Quickly select from your saved contacts</p>
              </div>

              {beneficiaries.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                  </div>
                  <p className="text-neutral-500">No saved beneficiaries</p>
                  <p className="text-sm text-neutral-400 mt-1">Add beneficiaries for faster transfers</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {beneficiaries.map((beneficiary) => (
                    <button
                      key={beneficiary.id}
                      onClick={() => selectBeneficiary(beneficiary.account_number)}
                      className="w-full text-left p-4 bg-neutral-50 rounded-xl hover:bg-primary-50 transition transform hover:scale-105 border border-transparent hover:border-primary-200 group"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-neutral-900 group-hover:text-primary-700">
                            {beneficiary.nickname || beneficiary.name}
                          </p>
                          <p className="text-sm text-neutral-600 group-hover:text-primary-600">
                            {beneficiary.account_number}
                          </p>
                        </div>
                        <svg className="w-5 h-5 text-neutral-400 group-hover:text-primary-500 transform group-hover:translate-x-1 transition" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </Card>           
          </div>
        </div>
      </div>

      {showVerificationModal && <VerificationModal />}
    </div>
  );
};

export default Transfer;
